/**
 * Resolve a target URL relative to a base URL
 *
 * @param {string} from - The Base URL being resolved against
 * @param {string} to - The HREF URL being resolved
 * @returns {string} A target URL relative to a base URL.
 */
export const urlResolve = (from, to) => {
  const resolvedUrl = new URL(to, new URL(from, "resolve://"));
  if (resolvedUrl.protocol === "resolve:") {
    const { pathname, search, hash } = resolvedUrl;
    return pathname + search + hash;
  }
  return resolvedUrl.toString();
};

/**
 * Truncate a string until a certain limit
 *
 * @param {string} text - The text to be truncated
 * @param {number} limit - Number
 * @returns {string} A new string containing the specified part of the given string.
 */
export const truncate = (text, limit) => text.substring(0, limit);

/**
 * Get the level of the heading from a prismic richtext field
 *
 * @param {object} title - An object that contains the property richText with a property type
 * @returns {string} A new string containing the heading level.
 */
export const headingLevel = (title) => title.richText[0].type?.slice(-1);

/**
 * Represents a validation pattern object.
 *
 * @property {RegExp} email - The regular expression pattern for validating email addresses.
 * @property {RegExp} tel - The regular expression pattern for validating phone numbers.
 */
export const validationPattern = {
  email:
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
  tel: /\d/,
};

/**
 * Check if an email is valid
 *
 * @param {string} email - The email address to check
 * @returns {boolean} True/false for the validity of the given email
 */
export const isValidEmail = (email) => validationPattern.email.test(email);

/**
 * Check if a number is a valid phone
 *
 * @param {string} number - The phone number to check
 * @returns {boolean} True/false for the validity of the given input
 */
export const isValidPhone = (phone) => validationPattern.tel.test(phone);

/**
 * Get the ordinal suffix for a given number.
 *
 * @param {number} n - The number to get the ordinal suffix for.
 * @param {string} l - The language code for localization. Defaults to "en-us".
 * @returns {string} The ordinal suffix for the given number.
 */
const ordinalEnSuffixes = new Map([
  ["one", "st"],
  ["two", "nd"],
  ["few", "rd"],
  ["other", "th"],
]);

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules#formatting_text_using_the_returned_tag
export const getNumberSuffix = (n, l = "en-us") => {
  if (l.startsWith("el")) return "ο";

  const enOrdinalRules = new Intl.PluralRules(l, { type: "ordinal" });
  const rule = enOrdinalRules.select(n);
  const suffix = ordinalEnSuffixes.get(rule);

  return suffix;
};

/**
 * Get the bootstrap "name" of a color
 *
 * @param {string} color - The color, usually one of [blue, black, white]
 * @returns {string} The bs color name, usually one of [primary, black, white]
 */
export const getBsColor = (color) => {
  if (!color) return "inherit";
  if (color === "blue") return "primary";
  return color;
};
